import { PlusIcon } from '@heroicons/react/24/outline';
import { motion } from 'motion/react';
import { useTranslation } from 'react-i18next';
import { twJoin } from 'tailwind-merge';

import { Button, Menu } from '@/components/ui/daisy';
import { ModalView } from '@/data/hook/question/useModalView';
import useToggle from '@/data/hook/useToggle';
import { Question } from '@/models/Question';
import QuestionSidebarItem from './QuestionSidebarItem';
import usePedWorkspace from '@/data/hook/ped-content/usePedWorkspace';

type QuestionSidebarProps = {
  questions: Question[];
  activeIndex: number;
  view: ModalView;
  onSelect: (index: number) => void;
  onAdd: () => void;
  onDelete: (index: number) => void;
};

const COLLAPSED_WIDTH = 60;
const EXPANDED_WIDTH = 220;

export default function QuestionSidebar({
  questions,
  activeIndex,
  onSelect,
  onAdd,
  view,
  onDelete,
}: QuestionSidebarProps) {
  const { isOpen, open, close } = useToggle();

  const { t } = useTranslation('translation', {
    keyPrefix: 'courseLineForm.questionsTab.createQuestionModal',
  });

  const { isLimitedEdition } = usePedWorkspace();

  return (
    <div className="shrink-0 z-20" style={{ width: COLLAPSED_WIDTH }}>
      <motion.div
        onMouseEnter={open}
        onMouseLeave={close}
        animate={{ width: isOpen ? EXPANDED_WIDTH : COLLAPSED_WIDTH }}
        transition={{ duration: 0.15, ease: 'linear' }}
        className="overflow-hidden rounded-2xl bg-base-100 p-2 shadow-sm h-fit"
      >
        <Menu size="sm" direction="vertical" className="w-full p-0">
          {questions.map((question, index) => (
            <QuestionSidebarItem
              key={question.identifier}
              question={question}
              number={index + 1}
              isActive={index === activeIndex}
              isExpanded={isOpen}
              onSelect={() => onSelect(index)}
              onDelete={() => onDelete(index)}
            />
          ))}

          <Menu.Item>
            <Button
              type="button"
              variant="ghost"
              color="primary"
              onClick={onAdd}
              className={twJoin(
                'font-normal',
                view !== 'create' ? 'not-hover:text-base-content' : '',
              )}
              active={view === 'create' && activeIndex === -1}
              disabled={isLimitedEdition}
            >
              <PlusIcon className="size-5 shrink-0" />
              {isOpen && (
                <span className="flex-1 truncate text-left text-sm">
                  {t('newQuestion')}
                </span>
              )}
            </Button>
          </Menu.Item>
        </Menu>
      </motion.div>
    </div>
  );
}
